import { SystemEntity } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import { FileTextIcon } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { useDocumentOcrResultPreview } from '@/api/rpc/hooks/core/useDocuments';
import { T, Var } from 'gt-react';

interface CoreDocumentOcrResultPreviewProps {
  entityType: SystemEntity; // Required by preview registry
  entityId: string;
  className?: string;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (status === 'completed') return 'default';
  if (status === 'failed') return 'destructive';
  if (status === 'processing') return 'secondary';
  return 'outline';
}

export function CoreDocumentOcrResultPreview({
  entityId,
  className,
}: CoreDocumentOcrResultPreviewProps) {
  const { data, isLoading, error } = useDocumentOcrResultPreview(entityId);

  if (isLoading) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <FileTextIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>OCR Result</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Loading...</T>
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('min-w-64 max-w-sm p-3', className)}>
        <div className="flex items-center gap-2 mb-2">
          <FileTextIcon className="size-4 text-muted-foreground" />
          <h4 className="text-sm font-semibold">
            <T>OCR Result</T>
          </h4>
        </div>
        <p className="text-xs text-muted-foreground">
          <T>Unable to load OCR result details</T>
        </p>
      </div>
    );
  }

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-3">
        <FileTextIcon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">
          <T>OCR Result</T>
        </h4>
      </div>

      <div className="space-y-2 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">
            <T>Status:</T>
          </span>
          <Badge variant={getStatusVariant(data.status)} className="text-xs">
            {data.status}
          </Badge>
        </div>
        <div>
          <span className="text-muted-foreground">
            <T>Engine:</T>
          </span>
          <span className="font-medium">
            {data.vendor} ({data.modelVersion})
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">
            <T>Processed:</T>
          </span>
          <span className="font-medium">
            <T context="formatted date and time when the OCR document was processed">
              <Var>
                {format(new Date(data.createdAt), 'MMM d, yyyy h:mm a')}
              </Var>
            </T>
          </span>
        </div>
        {data.error && (
          <div>
            <span className="text-muted-foreground">
              <T>Error:</T>
            </span>
            <span className="font-medium text-destructive">{data.error}</span>
          </div>
        )}
        {data.extractedTextPreview && (
          <div>
            <span className="text-muted-foreground">
              <T>Preview:</T>
            </span>
            <p className="mt-1 text-muted-foreground line-clamp-4">
              {data.extractedTextPreview}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
